import { useMemo } from 'react';
import { useLanguageStore } from '$/store/languageStore';
import type { SmartcatTranslationText } from '$/store/languageStore/smartcatTranslation';
import { myIpCopyKeys } from './myIpCopyKeys';
import {
  temporaryMyIpListCopy, temporaryMyIpDetailCopy, temporaryMyIpAssetCopy,
  temporaryMyIpEditCopy, temporaryMyIpMediaCopy, temporaryMyIpRoleCardCopy,
  temporaryMyIpUnavailableReason,
} from './temporaryCopy';

type CopyKeyMap = { [key: string]: string | CopyKeyMap; };

/** 只替换已下发的字符串，嵌套空态/校验文案逐项回退，并保留现有格式化函数。 */
function localizeCopy<T extends object>(source: T, keys: CopyKeyMap, translate: SmartcatTranslationText): T {
  const result = { ...source } as Record<string, unknown>;
  Object.entries(keys).forEach(([property, key]) => {
    const fallback = result[property];
    if (typeof key === 'string' && typeof fallback === 'string') {
      result[property] = translate((value) => value[key]) || fallback;
    } else if (typeof key === 'object' && fallback && typeof fallback === 'object') {
      result[property] = localizeCopy(fallback, key, translate);
    }
  });
  return result as T;
}

/** IP 专属文案使用正式 Smartcat key；通用文案保持既有优先级，资源未就绪时保留已授权回退。 */
export function useMyIpCopy() {
  const languageStore = useLanguageStore((state) => state.languageStore);
  return useMemo(() => {
    const translate = languageStore.smartcatTranslationText;
    const localized = {
      list: localizeCopy(temporaryMyIpListCopy, { ...myIpCopyKeys.shared, ...myIpCopyKeys.list }, translate),
      detail: localizeCopy(temporaryMyIpDetailCopy, { ...myIpCopyKeys.shared, ...myIpCopyKeys.detail }, translate),
      asset: localizeCopy(temporaryMyIpAssetCopy, { ...myIpCopyKeys.shared, ...myIpCopyKeys.asset }, translate),
      edit: localizeCopy(temporaryMyIpEditCopy, myIpCopyKeys.edit, translate),
      media: localizeCopy(temporaryMyIpMediaCopy, myIpCopyKeys.media, translate),
    };
    /** SDK 宿主可能尚未注入语言包，空译文不能覆盖可操作的按钮文案。 */
    const text = (getText: Parameters<typeof translate>[0], fallback: string) => translate(getText) || fallback;
    const projectCountTemplate = translate((value) => value.RoboNeo_Web_ip_creation_projectCount);
    /** 正式模板仅替换真实项目数；资源未下发时保留既有格式化函数。 */
    const formatProjectCount = projectCountTemplate
      ? (count: number) => projectCountTemplate.replace(/\{count\}/g, String(count))
      : temporaryMyIpListCopy.formatProjectCount;
    const shared = {
      formatProjectCount,
      loading: text((value) => value.RoboNeo_Web_loading1, localized.list.loading),
      retry: text((value) => value.roboneo_web_retry, localized.list.retry),
      loadError: text((value) => value.roboneo_web_loading_failed, localized.list.loadError),
      actionError: text((value) => value.roboneo_failed_try_again, localized.list.actionError),
    };
    const loadMore = text((value) => value.roboneo_web_more, localized.list.loadMore);
    return {
      unavailableReason: text((value) => value.RoboNeo_Web_ip_action_unavailable, temporaryMyIpUnavailableReason),
      list: { ...localized.list, ...shared, loadMore },
      detail: {
        ...localized.detail,
        ...shared,
        returnHome: text((value) => value.roboneo_web_go_back_home, localized.detail.returnHome),
        editAgent: text((value) => value.roboneo_web_edit1, localized.detail.editAgent),
        characterSettings: text((value) => value.character_setting, localized.detail.characterSettings),
        threeViews: text((value) => value.three_view_image, localized.detail.threeViews),
        loadMoreProjects: text((value) => value.roboneo_web_more, localized.detail.loadMoreProjects),
      },
      roleCard: {
        ...temporaryMyIpRoleCardCopy,
        close: text((value) => value.close, temporaryMyIpRoleCardCopy.close),
        loadError: text((value) => value.roboneo_web_loading_failed, temporaryMyIpRoleCardCopy.loadError),
        saveError: text((value) => value['178'], temporaryMyIpRoleCardCopy.saveError),
        view: text((value) => value[myIpCopyKeys.roleCard.view], temporaryMyIpRoleCardCopy.view),
        empty: text((value) => value[myIpCopyKeys.roleCard.empty], temporaryMyIpRoleCardCopy.empty),
        loading: text((value) => value.RoboNeo_Web_loading1, temporaryMyIpRoleCardCopy.loading),
        retry: text((value) => value.roboneo_web_retry, temporaryMyIpRoleCardCopy.retry),
        saveToLocal: text((value) => value.roboneo_web_download, temporaryMyIpRoleCardCopy.saveToLocal),
        coverUnavailable: text(
          (value) => value.RoboNeo_Web_ip_shared_coverUnavailable,
          temporaryMyIpRoleCardCopy.coverUnavailable,
        ),
      },
      asset: {
        ...localized.asset,
        ...shared,
        loadMore,
        networkError: text((value) => value['47'], localized.asset.networkError),
        searchAction: text((value) => value.roboneo_web_search, localized.asset.searchAction),
        detail: text((value) => value.material_details, localized.asset.detail),
      },
      edit: {
        ...localized.edit,
        saveError: text((value) => value.roboneo_web_saved_failed, localized.edit.saveError),
        cancel: text((value) => value.cancel, localized.edit.cancel),
        save: text((value) => value.roboneo_web_save, localized.edit.save),
        saving: text((value) => value.saving_, localized.edit.saving),
      },
      media: {
        ...localized.media,
        all: text((value) => value.roboneo_web_All, localized.media.all),
        image: text((value) => value.roboneo_web_image, localized.media.image),
        video: text((value) => value.roboneo_web_video, localized.media.video),
        audio: text((value) => value.roboneo_web_audio, localized.media.audio),
        unknown: text((value) => value.elements, localized.media.unknown),
      },
    };
  }, [languageStore]);
}
